import * as React from 'react';
import { Course } from './MobilePreview';

interface UseMobilePreviewProps {
  initialMode?: 'poster' | 'cover' | 'both';
  courses?: Course[];
}

export const useMobilePreview = ({ 
  initialMode = 'both', 
  courses = [] 
}: UseMobilePreviewProps = {}) => {
  const [currentMode, setCurrentMode] = React.useState<'poster' | 'cover' | 'both'>(initialMode);
  const [selectedCourse, setSelectedCourse] = React.useState<Course | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Função para converter dados do Rails/GraphQL para o formato do componente
  const formatCoursesData = React.useCallback((rawCourses: any[]): Course[] => {
    return rawCourses.map(course => ({
      id: course.id?.toString() || '',
      title: course.title || 'Sem título',
      description: course.description || course.resume || 'Sem descrição',
      cover: course.cover?.uri ? {
        uri: course.cover.uri,
        uri_original: course.cover.uri_original || course.cover.uri
      } : undefined,
      poster: course.poster?.uri ? {
        uri: course.poster.uri,
        uri_original: course.poster.uri_original || course.poster.uri
      } : undefined
    }));
  }, []);

  // Estado para armazenar cursos formatados
  const [formattedCourses, setFormattedCourses] = React.useState<Course[]>([]);

  // Efeito para formatar cursos quando os dados mudarem
  React.useEffect(() => {
    if (courses.length > 0) {
      setIsLoading(true);
      const formatted = formatCoursesData(courses);
      setFormattedCourses(formatted);
      setIsLoading(false);
    }
  }, [courses, formatCoursesData]);

  // Função para trocar modo de visualização
  const changeMode = React.useCallback((mode: 'poster' | 'cover' | 'both') => {
    setCurrentMode(mode);
    setSelectedCourse(null); // Limpa seleção ao trocar modo
  }, []);

  // Função para selecionar curso
  const selectCourse = React.useCallback((course: Course | null) => {
    setSelectedCourse(course);
  }, []);

  // Função para obter estatísticas dos cursos
  const getStats = React.useCallback(() => {
    const total = formattedCourses.length;
    const withCover = formattedCourses.filter(c => c.cover?.uri).length;
    const withPoster = formattedCourses.filter(c => c.poster?.uri).length;
    const withBoth = formattedCourses.filter(c => c.cover?.uri && c.poster?.uri).length;

    return {
      total,
      withCover,
      withPoster,
      withBoth,
      percentageWithCover: total > 0 ? Math.round((withCover / total) * 100) : 0,
      percentageWithPoster: total > 0 ? Math.round((withPoster / total) * 100) : 0,
      percentageWithBoth: total > 0 ? Math.round((withBoth / total) * 100) : 0
    };
  }, [formattedCourses]);

  // Função para filtrar cursos baseado no modo atual
  const getFilteredCourses = React.useCallback(() => {
    switch (currentMode) {
      case 'poster':
        return formattedCourses.filter(course => course.poster?.uri);
      case 'cover':
        return formattedCourses.filter(course => course.cover?.uri);
      case 'both':
        return formattedCourses.filter(course => course.cover?.uri || course.poster?.uri);
      default:
        return formattedCourses;
    }
  }, [formattedCourses, currentMode]);

  return {
    // Estados
    currentMode,
    selectedCourse,
    isLoading,
    formattedCourses,
    
    // Funções
    changeMode,
    selectCourse,
    formatCoursesData,
    getStats,
    getFilteredCourses,
    
    // Dados computados
    stats: getStats(),
    filteredCourses: getFilteredCourses()
  };
};
